import { useEffect } from 'react'

interface WakeLockSentinelLike {
  release: () => Promise<void>
}

interface WakeLockLike {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>
}

/**
 * Keep the screen on while a game is live.
 *
 * Thinking about a position looks exactly like idling to a phone, so the
 * display dims and locks mid-move. The lock is dropped whenever the tab is
 * hidden and re-requested on return, because the browser releases it anyway and
 * holding a stale sentinel does nothing.
 *
 * Unsupported on some browsers (notably older iOS), where this is a no-op —
 * there is no polyfill worth having.
 */
export const useWakeLock = (active: boolean): void => {
  useEffect(() => {
    const api = (navigator as unknown as { wakeLock?: WakeLockLike }).wakeLock
    if (api === undefined || !active) return

    let sentinel: WakeLockSentinelLike | null = null
    let released = false

    const acquire = async (): Promise<void> => {
      if (released || document.visibilityState !== 'visible') return
      try {
        sentinel = await api.request('screen')
      } catch {
        /* Denied, or the document was not visible after all. */
      }
    }

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void acquire()
    }

    void acquire()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      released = true
      document.removeEventListener('visibilitychange', onVisibility)
      void sentinel?.release().catch(() => undefined)
    }
  }, [active])
}
